import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { CommentThread } from '@/components/CommentThread';
import { LeadDetailDrawer } from '@/components/LeadDetailDrawer';
import { NotificationCenter } from '@/components/admin/NotificationCenter';
import { DaasMetrics } from '@/components/admin/DaasMetrics';
import { ProjectPipelineView } from '@/components/admin/ProjectPipelineView';
import { CrmPipelineView } from '@/components/admin/CrmPipelineView';
import {
  LayoutDashboard, Package, Settings, LogOut, ChevronLeft, ChevronRight,
  TrendingUp, Clock, DollarSign, CheckCircle2, AlertCircle, Eye,
  ChevronDown, Send, FileText, Image, Code2, Link2, Plus, Upload,
  ArrowUpRight, RefreshCw, Search, X, MessageSquare, Zap, Gift, Loader2,
  Users, UserPlus, Trash2, Mail, ClipboardList, Briefcase, LayoutGrid,
  Megaphone, MousePointerClick, Activity, Ban, CalendarClock,
  BarChart3, FlaskConical, Save, ShieldCheck, Lock, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Admin Auth Helper ── */
const getAdminToken = () => sessionStorage.getItem('ml_admin_token');
const adminHeaders = () => ({ 'x-admin-token': getAdminToken() || '' });

const VALID_STATUSES = [
  { value: 'PENDING_PAYMENT', label: 'Pending Payment', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { value: 'AWAITING_DOWNPAYMENT', label: 'Awaiting Payment', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  { value: 'IN_DEVELOPMENT', label: 'In Development', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { value: 'READY_FOR_REVIEW', label: 'Ready for Review', color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { value: 'COMPLETED', label: 'Completed', color: 'text-green-600 bg-green-50 border-green-200' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'text-red-600 bg-red-50 border-red-200' },
];

const getStatusStyle = (status) => VALID_STATUSES.find(s => s.value === status) || VALID_STATUSES[0];

/* ═══════════════════════════════════════════
   ADMIN LOGIN GATE
   ═══════════════════════════════════════════ */
/* ── Admin PIN gate — front-door friction before login/dashboard ── */
const ADMIN_PIN = '110920';
const PIN_OK_KEY = 'ml_admin_pin_ok';

const AdminPinGate = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin === ADMIN_PIN) {
      sessionStorage.setItem(PIN_OK_KEY, 'true');
      setError('');
      onUnlock();
    } else {
      setError('Incorrect PIN. Please try again.');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-pin-gate">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-brand-orange/20 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-6 h-6 text-brand-orange" />
          </div>
          <h1 className="text-2xl font-bold text-white">Secure Access</h1>
          <p className="text-sm text-slate-400 mt-1">Enter your admin PIN to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="admin-pin-error">{error}</div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-300 mb-1.5 block">Admin PIN</label>
            <input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); if (error) setError(''); }}
              maxLength={12}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-lg tracking-[0.5em] text-center placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange/40"
              placeholder={'\u2022'.repeat(6)}
              data-testid="admin-pin-input"
            />
          </div>
          <button
            type="submit" disabled={!pin}
            className="w-full py-3 rounded-xl bg-brand-orange text-white text-sm font-bold hover:bg-brand-orange-dark transition-colors duration-200 disabled:opacity-50"
            data-testid="admin-pin-submit"
          >
            Unlock
          </button>
          <p className="text-[10px] text-slate-500 text-center flex items-center justify-center gap-1.5"><Lock className="w-3 h-3" /> Authorized personnel only</p>
        </form>
      </motion.div>
    </div>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const resp = await axios.post(`${API}/admin/login`, { username, password });
      sessionStorage.setItem('ml_admin_token', resp.data.token);
      sessionStorage.setItem('ml_admin_user', resp.data.username);
      onLogin(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-brand-blue/20 flex items-center justify-center mx-auto mb-4">
            <Zap className="w-6 h-6 text-brand-blue" />
          </div>
          <h1 className="text-2xl font-bold text-white">MediaLabs Ops</h1>
          <p className="text-sm text-slate-400 mt-1">Internal Admin Portal</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-slate-800 rounded-2xl border border-slate-700 p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">{error}</div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-300 mb-1.5 block">Username</label>
            <input
              type="text" value={username} onChange={e => setUsername(e.target.value)} required
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
              placeholder="admin" data-testid="admin-username"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-300 mb-1.5 block">Password</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)} required
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
              placeholder={'\u2022'.repeat(8)} data-testid="admin-password"
            />
          </div>
          <button
            type="submit" disabled={loading}
            className="w-full py-3 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors duration-200 disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? 'Signing in...' : 'Sign In to Ops Portal'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MISSION CONTROL (KPI Cards)
   ═══════════════════════════════════════════ */
const MissionControl = ({ stats, loading }) => {
  const kpis = [
    { label: 'Active Sprints', value: stats?.active_sprints || 0, icon: TrendingUp, color: 'text-brand-blue', bg: 'bg-brand-blue/10' },
    { label: 'Pending Payments', value: stats?.pending_payments || 0, icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { label: 'In Review', value: stats?.in_review || 0, icon: Eye, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Completed', value: stats?.completed || 0, icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Total Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: 'text-brand-orange', bg: 'bg-brand-orange/10' },
    { label: 'Total Orders', value: stats?.total_orders || 0, icon: Package, color: 'text-slate-300', bg: 'bg-slate-700' },
  ];

  return (
    <div data-testid="mission-control">
      <h2 className="text-lg font-bold text-white mb-5">Mission Control</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 rounded-xl border border-slate-700 p-4 sm:p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <Icon className={`w-4.5 h-4.5 ${kpi.color}`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{loading ? '...' : kpi.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{kpi.label}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   ORDER PIPELINE (Table)
   ═══════════════════════════════════════════ */
const OrderPipeline = ({ orders, loading, onSelectOrder, onRefresh, onStatusChange }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const filtered = orders.filter(o => {
    const matchSearch = !search || o.gig_title?.toLowerCase().includes(search.toLowerCase()) || o.order_id?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'ALL' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="order-pipeline">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-white">Order Pipeline</h2>
        <button onClick={onRefresh} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors" data-testid="refresh-orders">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search orders..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
            data-testid="search-orders"
          />
        </div>
        <select
          value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
          data-testid="filter-status"
        >
          <option value="ALL">All Statuses</option>
          {VALID_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Order</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden sm:table-cell">Gig</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden md:table-cell">Payment</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden lg:table-cell">Deposit</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading orders...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No orders found</td></tr>
              ) : (
                filtered.map((order) => {
                  const statusStyle = getStatusStyle(order.status);
                  return (
                    <tr key={order.order_id} className="border-b border-slate-700/50 hover:bg-slate-750 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium text-xs">{order.order_id}</p>
                        <p className="text-slate-500 text-[11px] sm:hidden">{order.gig_title}</p>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <p className="text-slate-200 text-xs">{order.gig_title}</p>
                        <p className="text-slate-500 text-[11px]">{order.tier_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-semibold border ${statusStyle.color}`}>
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-xs text-slate-300">{order.payment_gateway || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs font-semibold text-green-400">${order.deposit?.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => onSelectOrder(order)}
                            className="p-1.5 rounded-lg bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20 transition-colors"
                            data-testid={`view-order-${order.order_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <StatusDropdown order={order} onChange={onStatusChange} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ── Status Dropdown ── */
const StatusDropdown = ({ order, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1.5 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors" data-testid={`status-dropdown-${order.order_id}`}>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-slate-700 rounded-xl border border-slate-600 shadow-xl z-50 overflow-hidden">
          {VALID_STATUSES.map(s => (
            <button
              key={s.value}
              onClick={() => { onChange(order.order_id, s.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-600 transition-colors ${
                order.status === s.value ? 'text-brand-blue font-semibold' : 'text-slate-300'
              }`}
              data-testid={`set-status-${s.value}`}
            >
              {s.label} {order.status === s.value && '\u2713'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   DELIVERABLE ENGINE (Per-Order Detail)
   ═══════════════════════════════════════════ */
const OrderDetail = ({ order, onClose, onRefresh }) => {
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('document');
  const [newUrl, setNewUrl] = useState('');
  const [newNote, setNewNote] = useState('');
  const [noteText, setNoteText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);

  const handleAddDeliverable = async () => {
    if (!newTitle.trim()) return;
    setUploading(true);
    try {
      await axios.post(`${API}/admin/orders/${order.order_id}/deliverables`, {
        title: newTitle, type: newType, url: newUrl || null, note: newNote || null,
      }, { headers: adminHeaders() });
      setNewTitle(''); setNewUrl(''); setNewNote('');
      onRefresh();
    } catch (err) {
      setNewTitle('Upload failed: ' + (err.response?.data?.detail || 'Try again'));
    } finally {
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSendingNote(true);
    try {
      await axios.post(`${API}/admin/orders/${order.order_id}/notes`, { text: noteText }, { headers: adminHeaders() });
      setNoteText('');
      onRefresh();
    } catch (err) {
      setNoteText('Failed to send: ' + (err.response?.data?.detail || 'Try again'));
    } finally {
      setSendingNote(false);
    }
  };

  const typeIcons = { document: FileText, design: Image, code: Code2, link: Link2 };
  const statusStyle = getStatusStyle(order.status);

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      data-testid="order-detail-panel"
    >
      <div className="flex items-center justify-between mb-5">
        <button onClick={onClose} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
          <ChevronLeft className="w-4 h-4" /> Back to Pipeline
        </button>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-semibold border ${statusStyle.color}`}>
          {statusStyle.label}
        </span>
      </div>

      {/* Order header */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
        <h3 className="text-lg font-bold text-white mb-1" data-testid="detail-order-title">{order.gig_title}</h3>
        <p className="text-xs text-slate-400">{order.order_id} &middot; {order.tier_name} &middot; ${order.total?.toLocaleString()}</p>
        <div className="flex gap-4 mt-3">
          <div><span className="text-[10px] text-slate-500 uppercase">Deposit</span><p className="text-sm font-bold text-green-400">${order.deposit?.toLocaleString()}</p></div>
          <div><span className="text-[10px] text-slate-500 uppercase">Gateway</span><p className="text-sm font-semibold text-slate-200">{order.payment_gateway || 'N/A'}</p></div>
          <div><span className="text-[10px] text-slate-500 uppercase">TX ID</span><p className="text-sm font-mono text-slate-300 truncate max-w-[120px]">{order.transaction_id || 'N/A'}</p></div>
        </div>
      </div>

      {/* Current Deliverables */}
      <div className="mb-5">
        <h4 className="text-sm font-bold text-white mb-3">Deliverables ({order.deliverables?.length || 0})</h4>
        <div className="space-y-2">
          {(order.deliverables || []).map((d) => {
            const Icon = typeIcons[d.type] || FileText;
            return (
              <div key={d.id} className="flex items-center gap-3 bg-slate-800 rounded-lg border border-slate-700 px-4 py-3">
                <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-200 truncate">{d.title}</p>
                  {d.url && <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-blue hover:underline truncate block">{d.url}</a>}
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${
                  d.status === 'approved' ? 'text-green-400 bg-green-500/10' : d.status === 'revision' ? 'text-orange-400 bg-orange-500/10' : 'text-slate-400 bg-slate-700'
                }`}>{d.status}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Deliverable */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Upload className="w-4 h-4 text-brand-blue" /> Upload Deliverable</h4>
        <div className="space-y-3">
          <input
            value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Deliverable title (e.g., 'Homepage Mockup v2')" 
            className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
            data-testid="deliverable-title-input"
          />
          <div className="flex gap-2">
            <select value={newType} onChange={e => setNewType(e.target.value)} className="px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none" data-testid="deliverable-type-select">
              <option value="document">Document</option>
              <option value="design">Design</option>
              <option value="code">Code</option>
              <option value="link">Link</option>
            </select>
            <input
              value={newUrl} onChange={e => setNewUrl(e.target.value)}
              placeholder="URL or link (optional)"
              className="flex-1 px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
              data-testid="deliverable-url-input"
            />
          </div>
          {/* Drop zone UI */}
          <div className="border-2 border-dashed border-slate-600 rounded-xl p-6 text-center hover:border-brand-blue/40 transition-colors cursor-pointer">
            <Upload className="w-6 h-6 text-slate-500 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Drag & drop files here, or click to browse</p>
            <p className="text-[10px] text-slate-600 mt-1">UI mockups, ZIP files, or documents</p>
          </div>
          <button
            onClick={handleAddDeliverable} disabled={!newTitle.trim() || uploading}
            className="w-full py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark transition-colors disabled:opacity-40"
            data-testid="add-deliverable-btn"
          >
            {uploading ? 'Adding...' : 'Add Deliverable'}
          </button>
        </div>
      </div>

      {/* Add Update Note */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-brand-orange" /> Send Update Note</h4>
        <textarea
          value={noteText} onChange={e => setNoteText(e.target.value)}
          rows={3} placeholder="Write a progress update for the client..."
          className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 resize-none"
          data-testid="admin-note-input"
        />
        <button
          onClick={handleAddNote} disabled={!noteText.trim() || sendingNote}
          className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-semibold hover:bg-brand-orange-dark transition-colors disabled:opacity-40"
          data-testid="send-note-btn"
        >
          <Send className="w-3.5 h-3.5" /> {sendingNote ? 'Sending...' : 'Send Note'}
        </button>
      </div>

      {/* Client's Project Brief */}
      {order.project_brief && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
          <h4 className="text-sm font-bold text-white mb-3">Client's Project Brief</h4>
          <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{order.project_brief}</p>
        </div>
      )}

      {/* Client's Uploaded Assets */}
      {order.assets && order.assets.length > 0 && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
          <h4 className="text-sm font-bold text-white mb-3">Client Assets ({order.assets.length})</h4>
          <div className="space-y-2">
            {order.assets.map(asset => (
              <div key={asset.id} className="flex items-center gap-3 bg-slate-700/50 rounded-lg px-3 py-2">
                <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="text-xs text-slate-300 flex-1 truncate">{asset.filename}</span>
                <span className="text-[10px] text-slate-500">{(asset.size / 1024).toFixed(0)}KB</span>
                <a href={`${API.replace('/api', '')}${asset.url}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-blue font-bold hover:underline">Download</a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Communication Thread */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-brand-blue" /> Project Updates</h4>
        <CommentThread orderId={order.order_id} currentUser="admin" currentName="MediaLabs Admin" />
      </div>

      {/* Admin Notes History */}
      {order.admin_notes && order.admin_notes.length > 0 && (
        <div className="mb-5">
          <h4 className="text-sm font-bold text-white mb-3">Notes History</h4>
          <div className="space-y-2">
            {order.admin_notes.map((note, i) => (
              <div key={note.id || i} className="bg-slate-800/60 rounded-lg border border-slate-700 px-4 py-3">
                <p className="text-xs text-slate-200">{note.text}</p>
                <p className="text-[10px] text-slate-500 mt-1">{new Date(note.timestamp).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

/* ═══════════════════════════════════════════
   CUSTOM OFFERS VIEW
   ═══════════════════════════════════════════ */
const CustomOffersView = () => {
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [price, setPrice] = useState('');
  const [cycle, setCycle] = useState('one-time');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    axios.get(`${API}/admin/offers`, { headers: adminHeaders() })
      .then(r => { setOffers(r.data.offers || []); setRequests(r.data.requests || []); })
      .catch((e) => { console.error('Admin: offers fetch failed (non-critical)', e); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSending(true); setErr(''); setResult(null);
    try {
      const resp = await axios.post(`${API}/admin/offers/create`, {
        client_email: email, title, description: desc, price_usd: parseFloat(price), billing_cycle: cycle,
      }, { headers: adminHeaders() });
      setResult(resp.data);
      setEmail(''); setTitle(''); setDesc(''); setPrice('');
    } catch (error) {
      setErr(error.response?.data?.detail || 'Failed to create offer');
    } finally { setSending(false); }
  };

  return (
    <div data-testid="custom-offers-view">
      <h2 className="text-lg font-bold text-white mb-5">Custom Offers</h2>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Create Form */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
          <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Gift className="w-4 h-4 text-brand-orange" /> Generate Custom Offer</h3>
          {result && (
            <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-xs text-green-400 font-semibold">Offer created! Link sent to client.</p>
              <p className="text-[10px] text-green-400/70 mt-1 break-all">{result.checkout_url}</p>
            </div>
          )}
          {err && <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20"><p className="text-xs text-red-400">{err}</p></div>}
          <form onSubmit={handleCreate} className="space-y-3">
            <input value={email} onChange={e => setEmail(e.target.value)} required placeholder="Client email" type="email"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid="offer-email" />
            <input value={title} onChange={e => setTitle(e.target.value)} required placeholder="Offer title (e.g., Dedicated VA - 20hrs/week)"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid="offer-title" />
            <textarea value={desc} onChange={e => setDesc(e.target.value)} required rows={2} placeholder="Scope of work"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none resize-none focus:ring-2 focus:ring-brand-blue/40" data-testid="offer-desc" />
            <div className="flex gap-2">
              <input value={price} onChange={e => setPrice(e.target.value)} required placeholder="Price (USD)" type="number" min="1" step="0.01"
                className="flex-1 px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid="offer-price" />
              <select value={cycle} onChange={e => setCycle(e.target.value)}
                className="px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none" data-testid="offer-cycle">
                <option value="one-time">One-Time</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
            <button type="submit" disabled={sending}
              className="w-full py-2.5 rounded-xl bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange-dark transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="offer-create-btn">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Send className="w-4 h-4" /> Create & Email Offer</>}
            </button>
          </form>
        </div>

        {/* Recent Requests + Offers */}
        <div className="space-y-4">
          {requests.length > 0 && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <h3 className="text-sm font-bold text-white mb-3">Client Requests ({requests.length})</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {requests.map(r => (
                  <div key={r.id} className="bg-slate-700/50 rounded-lg p-3 text-xs">
                    <div className="flex justify-between"><span className="text-slate-300">{r.client_email}</span><span className="text-brand-blue font-bold">{r.hours}h/{r.commitment}</span></div>
                    <p className="text-slate-400 mt-1 line-clamp-2">{r.scope}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {offers.length > 0 && (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
              <h3 className="text-sm font-bold text-white mb-3">Sent Offers ({offers.length})</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {offers.map(o => (
                  <div key={o.offer_id} className="bg-slate-700/50 rounded-lg p-3 text-xs">
                    <div className="flex justify-between"><span className="text-slate-300">{o.title}</span><span className="text-brand-orange font-bold">${o.price_usd}</span></div>
                    <div className="flex justify-between mt-1"><span className="text-slate-500">{o.client_email}</span><span className={`font-semibold ${o.status === 'accepted' ? 'text-green-400' : 'text-yellow-400'}`}>{o.status}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   USER DIRECTORY VIEW
   ═══════════════════════════════════════════ */
const UserDirectoryView = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [offerModal, setOfferModal] = useState(null); // user object or null
  const [offerTitle, setOfferTitle] = useState('');
  const [offerDesc, setOfferDesc] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // user to delete
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/users`, { headers: adminHeaders() })
      .then(r => setUsers(r.data.users || []))
      .catch(() => { /* non-critical */ })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = users.filter(u => !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()));

  const handleSendOffer = async () => {
    if (!offerModal?.email || !offerTitle) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/users/offer`, { client_email: offerModal.email, title: offerTitle, description: offerDesc, price: parseFloat(offerPrice) || 0 }, { headers: adminHeaders() });
      setSent(true);
      setTimeout(() => { setOfferModal(null); setSent(false); setOfferTitle(''); setOfferDesc(''); setOfferPrice(''); }, 1500);
    } catch (err) { console.error('Admin: send offer failed', err); }
    finally { setSending(false); }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget?.email) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(deleteTarget.email)}`, { headers: adminHeaders() });
      setUsers(prev => prev.filter(u => u.email !== deleteTarget.email));
      setDeleteTarget(null);
    } catch (err) { console.error('Admin: delete offer failed', err); }
    finally { setDeleting(false); }
  };

  return (
    <div data-testid="user-directory">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-white">User Directory</h2>
        <span className="text-xs text-slate-500">{users.length} users</span>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email..." className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid="user-search" />
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden sm:table-cell">Email</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Source</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Stage</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Date</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Action</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr> :
             filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No users found</td></tr> :
             filtered.map((u, i) => (
              <tr key={u.email || `user-${i}`} className="border-b border-slate-700/50 hover:bg-slate-750">
                <td className="px-4 py-3"><p className="text-white font-medium text-xs">{u.name || 'Unknown'}</p><p className="text-slate-500 text-[11px] sm:hidden">{u.email}</p></td>
                <td className="px-4 py-3 hidden sm:table-cell text-xs text-slate-300">{u.email || 'N/A'}</td>
                <td className="px-4 py-3 hidden md:table-cell"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${u.source === 'funnel_lead' ? 'bg-brand-orange/10 text-brand-orange' : 'bg-brand-blue/10 text-brand-blue'}`}>{u.source === 'funnel_lead' ? 'Funnel Lead' : 'Vision Brief'}</span></td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-400">{u.stage || '—'}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {u.email && <button onClick={() => setOfferModal(u)} className="px-3 py-1.5 rounded-lg bg-brand-blue/10 text-brand-blue text-[11px] font-semibold hover:bg-brand-blue/20 transition-colors" data-testid={`offer-btn-${i}`}><Send className="w-3 h-3 inline mr-1" />Offer</button>}
                    {u.email && <button onClick={() => setDeleteTarget(u)} className="px-2 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-[11px] hover:bg-red-500/20 transition-colors" data-testid={`delete-btn-${i}`}><Trash2 className="w-3 h-3" /></button>}
                  </div>
                </td>
              </tr>
            ))}\n          </tbody>
        </table>
      </div>

      {/* Insights & Offers Modal */}
      <AnimatePresence>
        {offerModal && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setOfferModal(null)} />
            <motion.div className="relative bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-md p-6" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-white">Send Offer to {offerModal.name}</h3>
                <button onClick={() => setOfferModal(null)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-700"><X className="w-4 h-4 text-slate-400" /></button>
              </div>
              <p className="text-xs text-slate-400 mb-4">{offerModal.email}</p>
              {sent ? (
                <div className="text-center py-6"><CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" /><p className="text-sm text-green-400 font-semibold">Offer Sent!</p></div>
              ) : (
                <div className="space-y-3">
                  <input value={offerTitle} onChange={e => setOfferTitle(e.target.value)} placeholder="Offer title" className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" />
                  <textarea value={offerDesc} onChange={e => setOfferDesc(e.target.value)} rows={3} placeholder="Description / recommendation..." className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none resize-none focus:ring-2 focus:ring-brand-blue/40" />
                  <input value={offerPrice} onChange={e => setOfferPrice(e.target.value)} type="number" placeholder="Price (USD)" className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" />
                  <button onClick={handleSendOffer} disabled={sending || !offerTitle} className="w-full py-2.5 rounded-xl bg-brand-orange text-white text-sm font-semibold hover:bg-brand-orange-dark transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Offer & Email Client
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteTarget(null)} />
            <motion.div className="relative bg-slate-800 rounded-2xl border border-red-500/20 w-full max-w-sm p-6 text-center" initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}>
              <Trash2 className="w-10 h-10 text-red-400 mx-auto mb-3" />
              <h3 className="text-base font-bold text-white mb-2">Delete User?</h3>
              <p className="text-xs text-slate-400 mb-5">This will permanently remove <strong className="text-slate-200">{deleteTarget.name || deleteTarget.email}</strong> and all their data (leads, orders, offers).</p>
              <div className="flex gap-2">
                <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl bg-slate-700 text-white text-sm font-semibold hover:bg-slate-600 transition-colors">Cancel</button>
                <button onClick={handleDeleteUser} disabled={deleting} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-1.5" data-testid="confirm-delete">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════
   TEAM MANAGEMENT VIEW
   ═══════════════════════════════════════════ */
const TeamManagementView = () => {
  const [team, setTeam] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchTeam = () => {
    axios.get(`${API}/admin/team`, { headers: adminHeaders() })
      .then(r => setTeam(r.data.team || []))
      .catch(() => { /* non-critical */ });
  };

  useEffect(() => { fetchTeam(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!newEmail) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/team`, { action: 'add', email: newEmail, name: newName }, { headers: adminHeaders() });
      setNewEmail(''); setNewName('');
      fetchTeam();
    } catch (err) { console.error('Admin: add team member failed', err); }
    finally { setLoading(false); }
  };

  const handleRemove = async (email) => {
    await axios.post(`${API}/admin/team`, { action: 'remove', email }, { headers: adminHeaders() }).catch(() => { /* remove error */ });
    fetchTeam();
  };

  const handleReset = async (email) => {
    await axios.post(`${API}/admin/team`, { action: 'reset_password', email }, { headers: adminHeaders() }).catch(() => { /* reset error */ });
  };

  return (
    <div data-testid="team-management">
      <h2 className="text-lg font-bold text-white mb-5">Team Management</h2>

      {/* Add member */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><UserPlus className="w-4 h-4 text-brand-blue" /> Add Sub-Admin</h3>
        <div className="flex gap-2">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name" className="flex-1 px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid="team-name" />
          <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="Email" type="email" className="flex-1 px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40" data-testid="team-email" />
          <button onClick={handleAdd} disabled={loading || !newEmail} className="px-4 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark transition-colors disabled:opacity-40" data-testid="team-add"><UserPlus className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Team list */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Email</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden sm:table-cell">Role</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
          </tr></thead>
          <tbody>
            {/* Super admin (non-removable) */}
            <tr className="border-b border-slate-700/50">
              <td className="px-4 py-3 text-xs text-white font-medium">Andrew Competente</td>
              <td className="px-4 py-3 text-xs text-slate-300">andrew@medialabs.ph</td>
              <td className="px-4 py-3 hidden sm:table-cell"><span className="text-[10px] font-bold px-2 py-0.5 rounded bg-brand-orange/10 text-brand-orange">SUPER ADMIN</span></td>
              <td className="px-4 py-3 text-right text-xs text-slate-500">Protected</td>
            </tr>
            {team.map(m => (
              <tr key={m.email} className="border-b border-slate-700/50 hover:bg-slate-750">
                <td className="px-4 py-3 text-xs text-white">{m.name || m.email}</td>
                <td className="px-4 py-3 text-xs text-slate-300">{m.email}</td>
                <td className="px-4 py-3 hidden sm:table-cell"><span className="text-[10px] font-bold px-2 py-0.5 rounded bg-brand-blue/10 text-brand-blue">Sub Admin</span></td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => handleReset(m.email)} className="px-2 py-1 rounded-lg bg-slate-700 text-slate-300 text-[10px] hover:bg-slate-600 transition-colors" title="Reset password"><Mail className="w-3 h-3 inline" /> Reset</button>
                    <button onClick={() => handleRemove(m.email)} className="px-2 py-1 rounded-lg bg-red-500/10 text-red-400 text-[10px] hover:bg-red-500/20 transition-colors" title="Remove"><Trash2 className="w-3 h-3 inline" /> Remove</button>
                  </div>
                </td>
              </tr>
            ))}
            {team.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500 text-xs">No sub-admins added yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   AUDIT LOG VIEW
   ═══════════════════════════════════════════ */
const AuditLogView = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/audit-log`, { headers: adminHeaders() })
      .then(r => setLogs(r.data.logs || []))
      .catch(() => { /* non-critical: audit log fetch failed */ })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="audit-log">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-white">Audit Log</h2>
        <span className="text-xs text-slate-500">{logs.length} entries</span>
      </div>
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Timestamp</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Action</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden sm:table-cell">Admin</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Target</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Details</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr> :
             logs.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No audit entries yet</td></tr> :
             logs.map((log) => (
              <tr key={log.id} className="border-b border-slate-700/50">
                <td className="px-4 py-3 text-xs text-slate-400">{new Date(log.timestamp).toLocaleString()}</td>
                <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                  log.action === 'DELETE_USER' ? 'bg-red-500/10 text-red-400' :
                  log.action === 'SEND_OFFER' ? 'bg-brand-orange/10 text-brand-orange' :
                  log.action === 'UPDATE_STATUS' ? 'bg-brand-blue/10 text-brand-blue' :
                  'bg-slate-700 text-slate-300'
                }`}>{log.action}</span></td>
                <td className="px-4 py-3 hidden sm:table-cell text-xs text-slate-400">{log.admin}</td>
                <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-300">{log.target}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500 max-w-[200px] truncate">{log.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   DATA HYGIENE / DANGER ZONE VIEW
   Smart Seed Purge — removes seed/dummy data, preserves real records
   ═══════════════════════════════════════════ */
const COLLECTION_LABELS = {
  leads: 'Leads',
  orders: 'Orders',
  invoices: 'Invoices',
  external_clients: 'External Clients',
  email_sequence_queue: 'Email Drip Queue',
  offers: 'Custom Offers',
  offer_requests: 'Offer Requests',
};

const DataHygieneView = () => {
  const [report, setReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [extraIds, setExtraIds] = useState('');
  const [expanded, setExpanded] = useState({});

  const parseIds = () => extraIds.split(',').map(s => s.trim()).filter(Boolean);

  const runScan = useCallback(async () => {
    setScanning(true);
    try {
      const resp = await axios.post(
        `${API}/admin/purge-seed/preview`,
        { extra_order_ids: extraIds.split(',').map(s => s.trim()).filter(Boolean) },
        { headers: adminHeaders() },
      );
      setReport(resp.data);
    } catch (err) {
      toast.error('Scan failed. Please try again.');
    } finally {
      setScanning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraIds]);

  useEffect(() => {
    runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = report?.total_matched || 0;
  const collections = report?.collections || {};
  const activeColls = Object.entries(collections).filter(([, v]) => v.matched > 0);
  const canPurge = confirmText.trim() === 'PURGE-SEED' && total > 0 && !purging;

  const handlePurge = async () => {
    if (!canPurge) return;
    setPurging(true);
    try {
      const resp = await axios.post(
        `${API}/admin/purge-seed/apply`,
        { confirm: 'PURGE-SEED', extra_order_ids: parseIds() },
        { headers: adminHeaders() },
      );
      toast.success(`Purged ${resp.data.total_deleted} seed record${resp.data.total_deleted === 1 ? '' : 's'}. Pipeline cleaned.`);
      setConfirmText('');
      await runScan();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed. Please try again.');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div data-testid="data-hygiene">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-brand-blue" /> Data Hygiene
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Smart Seed Purge — safely removes test/dummy data while preserving all real records.</p>
        </div>
        <button
          onClick={runScan}
          disabled={scanning}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm transition-colors disabled:opacity-50"
          data-testid="rescan-btn"
        >
          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Rescan
        </button>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-xl border border-brand-blue/20 bg-brand-blue/5 p-4 mb-5">
        <AlertCircle className="w-5 h-5 text-brand-blue shrink-0 mt-0.5" />
        <p className="text-xs text-slate-300 leading-relaxed">
          This tool <strong className="text-white">only</strong> targets records with explicit seed signals — test/demo/dummy emails
          (<code className="text-brand-blue">@example.com</code>, <code className="text-brand-blue">@mailinator.com</code>, <code className="text-brand-blue">test@…</code>),
          placeholder names (<code className="text-brand-blue">John Doe</code>, <code className="text-brand-blue">Acme</code>), demo orders
          (<code className="text-brand-blue">ORD-DEMO…</code>, simulated payments), and <strong className="text-white">&ldquo;Completed&rdquo; orders that never had a payment</strong> (Payment&nbsp;=&nbsp;<code className="text-brand-blue">N/A</code> — impossible for a real order).
          {' '}<strong className="text-white">Real paid orders, unpaid in-progress orders and legitimate clients are never touched.</strong>
        </p>
      </div>

      {/* Summary + breakdown */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-5">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-white">Seed records detected</span>
          <span
            className={`text-sm font-bold px-2.5 py-1 rounded-lg ${total > 0 ? 'bg-amber-500/10 text-amber-400' : 'bg-green-500/10 text-green-400'}`}
            data-testid="seed-total"
          >
            {scanning ? 'Scanning…' : `${total} record${total === 1 ? '' : 's'}`}
          </span>
        </div>

        {!scanning && total === 0 && (
          <div className="text-center py-8" data-testid="clean-state">
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
            <p className="text-sm text-green-400 font-semibold">Pipeline is clean</p>
            <p className="text-xs text-slate-500 mt-1">No seed or dummy data detected.</p>
          </div>
        )}

        {!scanning && total > 0 && (
          <div className="space-y-2">
            {activeColls.map(([coll, data]) => (
              <div key={coll} className="rounded-lg border border-slate-700 overflow-hidden">
                <button
                  onClick={() => setExpanded(p => ({ ...p, [coll]: !p[coll] }))}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-700/40 hover:bg-slate-700/70 transition-colors text-left"
                  data-testid={`seed-coll-${coll}`}
                >
                  <span className="text-sm text-slate-200 font-medium">{COLLECTION_LABELS[coll] || coll}</span>
                  <span className="inline-flex items-center gap-2">
                    <span className="text-xs font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">{data.matched}</span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded[coll] ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {expanded[coll] && (
                  <div className="divide-y divide-slate-700/50">
                    {data.samples.map((s, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs text-slate-200 truncate">{s.email || s.name || s.id}</p>
                          <p className="text-[11px] text-slate-500 truncate">
                            {s.id}{s.detail ? ` · ${s.detail}` : ''}{s.name && s.email ? ` · ${s.name}` : ''}
                          </p>
                        </div>
                        {typeof s.amount === 'number' && (
                          <span className="text-[11px] text-slate-400 shrink-0">${s.amount.toLocaleString()}</span>
                        )}
                      </div>
                    ))}
                    {data.matched > data.samples.length && (
                      <div className="px-4 py-2 text-[11px] text-slate-500">…and {data.matched - data.samples.length} more</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div className="bg-slate-800 rounded-xl border border-red-500/20 p-5">
        <h3 className="text-sm font-bold text-red-400 flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4" /> Danger Zone
        </h3>
        <p className="text-xs text-slate-400 mb-4">Permanently hard-deletes the seed records listed above. This cannot be undone.</p>

        <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Also remove specific order IDs (optional)</label>
        <input
          value={extraIds}
          onChange={e => setExtraIds(e.target.value)}
          onBlur={runScan}
          placeholder="e.g. ORD-4B5A4921, ORD-C990BD43"
          className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-blue/40 mb-4"
          data-testid="extra-order-ids"
        />

        <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
          Type <span className="text-red-400 font-bold">PURGE-SEED</span> to confirm
        </label>
        <input
          value={confirmText}
          onChange={e => setConfirmText(e.target.value)}
          placeholder="PURGE-SEED"
          className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500/40 mb-4"
          data-testid="confirm-input"
        />

        <button
          onClick={handlePurge}
          disabled={!canPurge}
          className="w-full py-3 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          data-testid="purge-btn"
        >
          {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          {total > 0 ? `Purge ${total} Seed Record${total === 1 ? '' : 's'}` : 'Nothing to Purge'}
        </button>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   INVOICING VIEW (Tab 7)
   ═══════════════════════════════════════════ */
const InvoicingView = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', company: '', email: '' });
  const [form, setForm] = useState({ client_email: '', client_name: '', company: '', issue_date: new Date().toISOString().split('T')[0], due_date: '', line_items: [{ description: '', qty: 1, price: 0 }], tax_rate: 0, notes: '', currency: 'USD' });
  const [creating, setCreating] = useState(false);

  const fetchData = () => {
    axios.get(`${API}/admin/invoices`, { headers: adminHeaders() }).then(r => setInvoices(r.data.invoices || [])).catch(() => { /* non-critical */ });
    axios.get(`${API}/admin/external-clients`, { headers: adminHeaders() }).then(r => setClients(r.data.clients || [])).catch(() => { /* non-critical */ });
  };
  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateClient = async () => {
    if (!newClient.email) return;
    await axios.post(`${API}/admin/external-clients`, newClient, { headers: adminHeaders() }).catch(() => { /* create error */ });
    setNewClient({ name: '', company: '', email: '' }); setShowClientModal(false); fetchData();
  };

  const addLineItem = () => setForm(p => ({ ...p, line_items: [...p.line_items, { description: '', qty: 1, price: 0 }] }));
  const updateLine = (i, field, val) => setForm(p => ({ ...p, line_items: p.line_items.map((item, idx) => idx === i ? { ...item, [field]: field === 'description' ? val : Number(val) } : item) }));
  const removeLine = (i) => setForm(p => ({ ...p, line_items: p.line_items.filter((_, idx) => idx !== i) }));

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/admin/invoices`, form, { headers: adminHeaders() });
      setShowCreate(false); fetchData();
      setForm(p => ({ ...p, line_items: [{ description: '', qty: 1, price: 0 }], notes: '' }));
    } catch (err) { console.error('Admin: create invoice failed', err); }
    finally { setCreating(false); }
  };

  const handleStatus = async (id, status, isRejection = false) => {
    await axios.patch(`${API}/admin/invoices/${id}/status`, { status, is_rejection: isRejection }, { headers: adminHeaders() }).catch(() => { /* status error */ });
    fetchData();
  };

  const statusColors = { draft: 'bg-slate-600 text-slate-200', sent: 'text-blue-400 bg-blue-500/10', viewed: 'text-purple-400 bg-purple-500/10', pending_verification: 'text-amber-400 bg-amber-500/10', paid: 'text-green-400 bg-green-500/10', overdue: 'text-red-400 bg-red-500/10' };

  return (
    <div data-testid="invoicing-view">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-white">Invoicing</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowClientModal(true)} className="px-3 py-2 rounded-lg bg-slate-700 text-white text-xs font-semibold hover:bg-slate-600 transition-colors inline-flex items-center gap-1.5" data-testid="add-client-btn"><Plus className="w-3.5 h-3.5" /> Add Client</button>
          <button onClick={() => setShowCreate(true)} className="px-3 py-2 rounded-lg bg-brand-blue text-white text-xs font-semibold hover:bg-brand-blue-dark transition-colors inline-flex items-center gap-1.5" data-testid="create-invoice-btn"><Plus className="w-3.5 h-3.5" /> New Invoice</button>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden mb-5">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-700">
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Invoice</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden sm:table-cell">Client</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Due</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Total</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Actions</th>
          </tr></thead>
          <tbody>
            {invoices.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500 text-xs">No invoices yet</td></tr> :
            invoices.map(inv => {
              const sym = inv.currency === 'PHP' ? '₱' : '$';
              return (
                <tr key={inv.invoice_id} className="border-b border-slate-700/50">
                  <td className="px-4 py-3"><p className="text-xs text-white font-medium">{inv.invoice_id}</p></td>
                  <td className="px-4 py-3 hidden sm:table-cell text-xs text-slate-300">{inv.client_name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-400">{inv.due_date}</td>
                  <td className="px-4 py-3 text-xs font-bold text-brand-orange">{sym}{(inv.total || 0).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${statusColors[inv.status] || ''}`}>{(inv.status || '').replace('_', ' ')}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {inv.status === 'draft' && <button onClick={() => handleStatus(inv.invoice_id, 'sent')} className="px-2 py-1 rounded bg-brand-blue/10 text-brand-blue text-[10px] font-bold hover:bg-brand-blue/20">Send</button>}
                      {inv.status === 'pending_verification' && <>
                        <button onClick={() => handleStatus(inv.invoice_id, 'paid')} className="px-2 py-1 rounded bg-green-500/10 text-green-400 text-[10px] font-bold hover:bg-green-500/20">Approve</button>
                        <button onClick={() => handleStatus(inv.invoice_id, 'sent', true)} className="px-2 py-1 rounded bg-red-500/10 text-red-400 text-[10px] font-bold hover:bg-red-500/20">Reject</button>
                      </>}
                      {inv.payment_proof_url && <a href={`${BACKEND_URL}${inv.payment_proof_url}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-slate-700 text-slate-300 text-[10px] hover:bg-slate-600">Proof</a>}
                      <a href={`/invoice/${inv.invoice_id}`} target="_blank" rel="noopener noreferrer" className="px-2 py-1 rounded bg-slate-700 text-slate-300 text-[10px] hover:bg-slate-600">View</a>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create Invoice Modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
            <motion.div className="relative bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" initial={{ scale: 0.95 }} animate={{ scale: 1 }}>
              <h3 className="text-sm font-bold text-white mb-4">Create Invoice</h3>
              <div className="space-y-3">
                <select value={form.client_email} onChange={e => { const c = clients.find(cl => cl.email === e.target.value); setForm(p => ({ ...p, client_email: e.target.value, client_name: c?.name || '', company: c?.company || '' })); }} className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm">
                  <option value="">Select client...</option>
                  {clients.map(c => <option key={c.email} value={c.email}>{c.name} ({c.email})</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[10px] text-slate-400 mb-1 block">Issue Date</label><input type="date" value={form.issue_date} onChange={e => setForm(p => ({ ...p, issue_date: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm" /></div>
                  <div><label className="text-[10px] text-slate-400 mb-1 block">Due Date</label><input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm" /></div>
                </div>
                <div>
                  <label className="text-[10px] text-slate-400 mb-1 block">Line Items</label>
                  {form.line_items.map((item, i) => (
                    <div key={`li-${i}`} className="flex gap-1.5 mb-1.5">
                      <input value={item.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" className="flex-1 px-2 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-xs" />
                      <input value={item.qty} onChange={e => updateLine(i, 'qty', e.target.value)} type="number" min="1" className="w-14 px-2 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-xs text-center" />
                      <input value={item.price} onChange={e => updateLine(i, 'price', e.target.value)} type="number" placeholder="Price" className="w-20 px-2 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white text-xs" />
                      {form.line_items.length > 1 && <button onClick={() => removeLine(i)} className="px-2 rounded-lg bg-red-500/10 text-red-400 text-xs">×</button>}
                    </div>
                  ))}
                  <button onClick={addLineItem} className="text-[10px] text-brand-blue font-semibold hover:underline">+ Add line</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="text-[10px] text-slate-400 mb-1 block">Tax %</label><input type="number" value={form.tax_rate} onChange={e => setForm(p => ({ ...p, tax_rate: Number(e.target.value) }))} className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm" /></div>
                  <div><label className="text-[10px] text-slate-400 mb-1 block">Currency</label><select value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm"><option value="USD">USD ($)</option><option value="PHP">PHP (₱)</option></select></div>
                </div>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Bank/check instructions..." className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500 resize-none" />
                <button onClick={handleCreate} disabled={creating || !form.client_email} className="w-full py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="submit-invoice">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Invoice'}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Client Modal */}
      <AnimatePresence>
        {showClientModal && (
          <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowClientModal(false)} />
            <motion.div className="relative bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-sm p-6" initial={{ scale: 0.95 }} animate={{ scale: 1 }}>
              <h3 className="text-sm font-bold text-white mb-4">Add External Client</h3>
              <div className="space-y-3">
                <input value={newClient.name} onChange={e => setNewClient(p => ({ ...p, name: e.target.value }))} placeholder="Full Name" className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500" />
                <input value={newClient.company} onChange={e => setNewClient(p => ({ ...p, company: e.target.value }))} placeholder="Company (optional)" className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500" />
                <input value={newClient.email} onChange={e => setNewClient(p => ({ ...p, email: e.target.value }))} type="email" placeholder="Email" className="w-full px-3 py-2.5 rounded-xl bg-slate-700 border border-slate-600 text-white text-sm placeholder-slate-500" />
                <button onClick={handleCreateClient} disabled={!newClient.email} className="w-full py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold disabled:opacity-40">Add Client</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════
   LEADS VIEW — Executive Applications + Detail Drawer
   ═══════════════════════════════════════════ */
const LEAD_STATUS_STYLES = {
  active: 'bg-brand-blue/10 text-brand-blue',
  booked: 'bg-green-500/10 text-green-400',
  completed: 'bg-purple-500/10 text-purple-400',
  unsubscribed: 'bg-red-500/10 text-red-400',
};

const LeadsView = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/leads`, { headers: adminHeaders() });
      setLeads(r.data.leads || []);
    } catch (err) {
      console.error('Admin: leads fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="leads-view">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-white">Executive Applications</h2>
          <p className="text-xs text-slate-500 mt-0.5">Click any application to view the full submission and drip engagement.</p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 bg-slate-800 border border-slate-700 hover:text-white hover:border-slate-600 transition-colors"
          data-testid="leads-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-700">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Budget</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Timeline</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Opens</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Clicks</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : !leads.length ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                  No applications yet. New funnel submissions appear here.
                </td></tr>
              ) : (
                leads.map((lead, i) => {
                  const app = lead.application || {};
                  const drip = lead.drip || {};
                  const status = drip.status || 'none';
                  return (
                    <tr
                      key={lead.id || `${lead.email}-${i}`}
                      onClick={() => setSelected(lead)}
                      className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors"
                      data-testid={`lead-row-${i}`}
                    >
                      <td className="px-4 py-3 text-xs font-medium text-slate-200 max-w-[160px] truncate">
                        {lead.name || '—'}
                        {lead.company && <span className="block text-[10px] text-slate-500 font-normal">{lead.company}</span>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-400 max-w-[200px] truncate">{lead.email || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-300">{app.budget || '—'}</td>
                      <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-400">{app.timeline || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${LEAD_STATUS_STYLES[status] || 'bg-slate-700 text-slate-300'}`}>
                          {status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-300">{drip.opens_total || 0}</td>
                      <td className="px-4 py-3 text-xs text-slate-300">{drip.clicks_total || 0}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LeadDetailDrawer lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
};


/* ═══════════════════════════════════════════
   MARKETING VIEW — 7-Day Drip Campaign metrics
   ═══════════════════════════════════════════ */
const STATUS_STYLES = {
  active: 'bg-brand-blue/10 text-brand-blue',
  booked: 'bg-green-500/10 text-green-400',
  completed: 'bg-purple-500/10 text-purple-400',
  unsubscribed: 'bg-red-500/10 text-red-400',
};

/* ═══════════════════════════════════════════
   7-DAY CEO FUNNEL — admin-driven (isolated from the lead-nurture drip above)
   Enroll any email → Day 1 sends instantly; Days 2–7 advance via the daily
   platform cron, or manually per-lead here.
   ═══════════════════════════════════════════ */
const FUNNEL_STATUS_STYLES = {
  active: 'bg-brand-blue/10 text-brand-blue',
  completed: 'bg-purple-500/10 text-purple-400',
  cancelled: 'bg-rose-500/10 text-rose-400',
};

const CeoFunnelPanel = () => {
  const [funnels, setFunnels] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active: 0, completed: 0, cancelled: 0 });
  const [loadingList, setLoadingList] = useState(true);
  const [tab, setTab] = useState('active'); // 'active' | 'history'

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [advancingId, setAdvancingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  const loadFunnels = useCallback(async () => {
    setLoadingList(true);
    try {
      const resp = await axios.get(`${API}/marketing/funnels?limit=200`, { headers: adminHeaders() });
      setFunnels(resp.data?.funnels || []);
      setCounts(resp.data?.counts || { total: 0, active: 0, completed: 0, cancelled: 0 });
    } catch (err) {
      console.error('Admin: funnel list fetch failed', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadFunnels(); }, [loadFunnels]);

  const enroll = async () => {
    const e = email.trim();
    if (!e || !e.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setEnrolling(true);
    try {
      const resp = await axios.post(
        `${API}/marketing/enroll-funnel`,
        { email: e, first_name: firstName.trim() },
        { headers: adminHeaders() },
      );
      if (resp.data?.day1_sent) {
        toast.success('Enrolled in 7-Day Funnel', { description: `Day 1 sent to ${e}` });
      } else {
        toast.warning('Enrolled — Day 1 not sent', {
          description: 'The email service did not accept Day 1 (check Resend quota). Use "Advance" to retry.',
        });
      }
      // Note: the Meta Pixel `Lead` conversion for funnel enrollment is fired
      // SERVER-SIDE via the Conversions API (backend /api/marketing/enroll-funnel)
      // so it is attributed to the prospect, not the admin's browser.
      setEmail('');
      setFirstName('');
      loadFunnels();
    } catch (err) {
      toast.error('Enrollment failed', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setEnrolling(false);
    }
  };

  const advanceLead = async (lead) => {
    setAdvancingId(lead.id);
    try {
      const resp = await axios.post(
        `${API}/marketing/funnel/${lead.id}/advance`,
        {},
        { headers: adminHeaders() },
      );
      toast.success(`Day ${resp.data?.day_sent} sent`, { description: `Advanced ${lead.email}` });
      loadFunnels();
    } catch (err) {
      toast.error('Could not advance lead', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setAdvancingId(null);
    }
  };

  const cancelLead = async (lead) => {
    if (!window.confirm(`Cancel the 7-day funnel for ${lead.email}? They will stop receiving emails.`)) return;
    setCancellingId(lead.id);
    try {
      await axios.post(`${API}/marketing/funnel/${lead.id}/cancel`, {}, { headers: adminHeaders() });
      toast.success('Funnel cancelled', { description: `${lead.email} moved to History` });
      loadFunnels();
    } catch (err) {
      toast.error('Could not cancel lead', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setCancellingId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch (e) { return '—'; }
  };

  const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch (e) { return '—'; }
  };

  const countChips = [
    { label: 'Total', value: counts.total ?? 0, testid: 'funnel-count-total', color: 'text-white' },
    { label: 'Active', value: counts.active ?? 0, testid: 'funnel-count-active', color: 'text-brand-blue' },
    { label: 'Completed', value: counts.completed ?? 0, testid: 'funnel-count-completed', color: 'text-purple-400' },
    { label: 'Cancelled', value: counts.cancelled ?? 0, testid: 'funnel-count-cancelled', color: 'text-rose-400' },
  ];

  const activeLeads = funnels.filter((f) => f.status === 'active');
  const historyLeads = funnels.filter((f) => f.status === 'completed' || f.status === 'cancelled');
  const visibleLeads = tab === 'active' ? activeLeads : historyLeads;

  const tabs = [
    { id: 'active', label: 'Active Leads', count: activeLeads.length },
    { id: 'history', label: 'History', count: historyLeads.length },
  ];

  return (
    <div
      className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden mb-6"
      data-testid="ceo-funnel-panel"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-800/40">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-brand-orange/10 flex items-center justify-center">
            <Zap className="w-4.5 h-4.5 text-brand-orange" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">7-Day CEO Funnel</h3>
            <p className="text-[11px] text-slate-500">Admin-driven sequence · Day 1 sends instantly, Days 2–7 advance daily by cron</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {countChips.map((c) => (
            <div key={c.label} className="text-center" data-testid={c.testid}>
              <p className={`text-lg font-bold leading-none ${c.color}`}>{c.value}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">{c.label}</p>
            </div>
          ))}
          <button
            onClick={loadFunnels}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 bg-slate-900 border border-slate-700 hover:text-white hover:border-slate-600 transition-colors"
            data-testid="funnel-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingList ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Enroll form */}
      <div className="px-5 py-4 border-b border-slate-700/60">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Lead Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enroll(); }}
              placeholder="ceo@company.com"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/15 transition-[border-color,box-shadow] duration-200"
              data-testid="funnel-enroll-email"
            />
          </div>
          <div className="w-full md:w-48">
            <label className="text-[11px] font-semibold text-slate-400 mb-1 block">First Name <span className="text-slate-600 font-normal">(optional)</span></label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enroll(); }}
              placeholder="e.g. Maria"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/15 transition-[border-color,box-shadow] duration-200"
              data-testid="funnel-enroll-firstname"
            />
          </div>
          <button
            onClick={enroll}
            disabled={enrolling}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-brand-orange text-white text-xs font-bold hover:bg-brand-orange-dark focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/30 disabled:opacity-40 transition-[background-color,box-shadow] duration-200 shrink-0"
            data-testid="funnel-enroll-submit"
          >
            {enrolling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Enroll in 7-Day Funnel
          </button>
        </div>
      </div>

      {/* Tabs: Active vs History */}
      <div className="flex items-center gap-1 px-5 pt-4" data-testid="funnel-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-colors ${
              tab === t.id
                ? 'bg-slate-900 text-white border border-slate-600'
                : 'text-slate-400 hover:text-white border border-transparent'
            }`}
            data-testid={`funnel-tab-${t.id}`}
          >
            {t.label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-brand-orange/20 text-brand-orange' : 'bg-slate-700 text-slate-400'}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Enrolled leads table */}
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-700">
            <th className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase">Lead Email</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Day Progress</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Enrolled</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Last Activity</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
            <th className="text-right px-5 py-3 text-xs font-semibold text-slate-400 uppercase">Action</th>
          </tr></thead>
          <tbody>
            {loadingList ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
            ) : !visibleLeads.length ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500" data-testid="funnel-empty">
                <Mail className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                {tab === 'active'
                  ? 'No active leads. Enroll an email above to start the 7-day sequence.'
                  : 'No completed or cancelled leads yet.'}
              </td></tr>
            ) : (
              visibleLeads.map((lead, i) => {
                const day = Number(lead.current_day || 0);
                const total = Number(lead.total_days || 7);
                const pct = Math.round((day / total) * 100);
                const isActive = lead.status === 'active';
                const canAdvance = isActive && day < total;
                return (
                  <tr key={lead.id} className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors" data-testid={`funnel-lead-row-${i}`}>
                    <td className="px-5 py-3 text-xs text-slate-200 max-w-[240px] truncate">
                      {lead.email}
                      {lead.first_name && <span className="text-slate-500"> · {lead.first_name}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 rounded-full bg-slate-700 overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-300 ${lead.status === 'cancelled' ? 'bg-rose-500' : 'bg-brand-orange'}`} style={{ width: `${day ? Math.max(pct, 8) : 0}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-300 whitespace-nowrap" data-testid={`funnel-lead-day-${i}`}>
                          {day === 0 ? 'Not sent' : `Day ${day}`} / {total}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-400">{fmtDate(lead.enrolled_at)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-400" data-testid={`funnel-lead-activity-${i}`}>{fmtDateTime(lead.last_action_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${FUNNEL_STATUS_STYLES[lead.status] || 'bg-slate-700 text-slate-300'}`} data-testid={`funnel-lead-status-${i}`}>
                        {(lead.status || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {isActive ? (
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => advanceLead(lead)}
                            disabled={!canAdvance || advancingId === lead.id}
                            title={!canAdvance ? 'Lead has completed the funnel' : 'Send the next day now'}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-[11px] font-bold text-brand-blue hover:border-brand-blue/50 hover:bg-brand-blue/5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            data-testid={`funnel-advance-${i}`}
                          >
                            {advancingId === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronRight className="w-3 h-3" />}
                            Advance
                          </button>
                          <button
                            onClick={() => cancelLead(lead)}
                            disabled={cancellingId === lead.id}
                            title="Cancel this funnel (stops all emails)"
                            className="inline-flex items-center justify-center p-1.5 rounded-lg bg-slate-900 border border-slate-700 text-rose-400 hover:border-rose-500/50 hover:bg-rose-500/5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-rose-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            data-testid={`funnel-cancel-${i}`}
                          >
                            {cancellingId === lead.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          </button>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-600">
                          {lead.status === 'completed' ? 'Sequence complete' : `Cancelled ${fmtDate(lead.cancelled_at)}`}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MarketingView = () => {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  // Send-window settings (admin-editable)
  const [win, setWin] = useState({ start: '09:00', end: '11:30', enabled: true });
  const [savingWin, setSavingWin] = useState(false);

  // Test send
  const [testDay, setTestDay] = useState(1);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, h, s] = await Promise.all([
        axios.get(`${API}/admin/marketing`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/health/drip`, { headers: adminHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/drip/settings`, { headers: adminHeaders() }).catch(() => ({ data: null })),
      ]);
      setData(m.data);
      setHealth(h.data);
      if (s.data) setWin({ start: s.data.start, end: s.data.end, enabled: s.data.enabled });
    } catch (err) {
      console.error('Admin: marketing metrics fetch failed', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveWindow = async () => {
    if (win.enabled && win.start >= win.end) {
      toast.error('Start time must be earlier than end time');
      return;
    }
    setSavingWin(true);
    try {
      await axios.put(`${API}/admin/drip/settings`, win, { headers: adminHeaders() });
      toast.success('Send window updated', {
        description: win.enabled ? `Emails send ${win.start}–${win.end} local time` : 'Window off — emails send anytime',
      });
      load();
    } catch (err) {
      toast.error('Could not save send window', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSavingWin(false);
    }
  };

  const sendTest = async () => {
    const email = testEmail.trim();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSendingTest(true);
    try {
      const resp = await axios.post(
        `${API}/admin/emails/test-send`,
        { day: Number(testDay), email },
        { headers: adminHeaders() },
      );
      toast.success(`Day ${resp.data.day} test sent`, { description: `Delivered to ${resp.data.email}` });
    } catch (err) {
      toast.error('Test send failed', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSendingTest(false);
    }
  };

  const eng = data?.engagement || {};
  const st = data?.status || {};

  const kpis = [
    { label: 'Total Enrolled', value: data?.total_enrolled ?? 0, icon: Mail, color: 'text-brand-blue', bg: 'bg-brand-blue/10' },
    { label: 'Active Sequences', value: st.active ?? 0, icon: Activity, color: 'text-brand-blue', bg: 'bg-brand-blue/10' },
    { label: 'Booked (Converted)', value: st.booked ?? 0, icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10' },
    { label: 'Completed', value: st.completed ?? 0, icon: CalendarClock, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { label: 'Unsubscribed', value: st.unsubscribed ?? 0, icon: Ban, color: 'text-red-400', bg: 'bg-red-500/10' },
    { label: 'Total Opens', value: eng.total_opens ?? 0, sub: `${eng.open_rate ?? 0}% of leads`, icon: Eye, color: 'text-brand-orange', bg: 'bg-brand-orange/10' },
    { label: 'Total Clicks', value: eng.total_clicks ?? 0, sub: `${eng.click_rate ?? 0}% of leads`, icon: MousePointerClick, color: 'text-brand-orange', bg: 'bg-brand-orange/10' },
    { label: 'Engaged Leads', value: `${eng.leads_opened ?? 0} / ${eng.leads_clicked ?? 0}`, sub: 'opened / clicked', icon: TrendingUp, color: 'text-slate-300', bg: 'bg-slate-700' },
  ];

  const perDay = data?.per_day || {};
  const dayMax = Math.max(1, ...Object.values(perDay).map((v) => Number(v) || 0));
  const days = [1, 2, 3, 4, 5, 6, 7];

  const perDayEngagement = data?.per_day_engagement || [];
  const chartData = perDayEngagement.map((r) => ({
    name: `Day ${r.day}`,
    Opens: r.opens || 0,
    Clicks: r.clicks || 0,
    sent: r.sent || 0,
    subject: r.subject || '',
  }));
  const hasEngagement = perDayEngagement.some((r) => (r.opens || r.clicks || r.sent));

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch (e) { return '—'; }
  };

  return (
    <div data-testid="marketing-view">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold text-white">Drip Campaign — Marketing</h2>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold text-slate-300 bg-slate-800 border border-slate-700 hover:text-white hover:border-slate-600 transition-colors"
          data-testid="marketing-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* 7-Day CEO Funnel — admin-driven (separate from the lead-nurture drip below) */}
      <CeoFunnelPanel />

      {/* Scheduler status banner */}
      {health && (
        <div
          className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-slate-800 rounded-xl border border-slate-700 px-4 py-3 mb-5"
          data-testid="drip-scheduler-status"
        >
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${health.scheduler_alive ? 'bg-green-400' : 'bg-red-400'}`} />
            <span className="text-xs font-semibold text-white">
              Scheduler {health.scheduler_alive ? 'Running' : 'Stopped'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <CalendarClock className="w-3.5 h-3.5 text-brand-orange" />
            Send window <span className="text-slate-200 font-medium">{health.send_window}</span>
            <span className="text-slate-500">({health.default_timezone} fallback)</span>
          </div>
          <div className="text-xs text-slate-400">
            Last tick <span className="text-slate-200 font-medium">{fmtDate(health.last_tick_at)}</span>
          </div>
          {health.window_bypassed && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">WINDOW BYPASSED</span>
          )}
          {health.dry_run && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">DRY RUN</span>
          )}
        </div>
      )}

      {/* Controls: Send Window + Test Send */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Send Window control */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5" data-testid="drip-send-window-card">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-brand-blue" />
            <h3 className="text-sm font-bold text-white">Send Window</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">Days 2–7 only send during these local hours. Day 1 always sends instantly.</p>

          <div className="flex items-end gap-3 mb-4">
            <div className="flex-1">
              <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Start</label>
              <input
                type="time"
                value={win.start}
                onChange={(e) => setWin((w) => ({ ...w, start: e.target.value }))}
                disabled={!win.enabled}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 disabled:opacity-40 transition-[border-color,box-shadow] duration-200 [color-scheme:dark]"
                data-testid="send-window-start"
              />
            </div>
            <span className="pb-3 text-slate-500 text-xs">to</span>
            <div className="flex-1">
              <label className="text-[11px] font-semibold text-slate-400 mb-1 block">End</label>
              <input
                type="time"
                value={win.end}
                onChange={(e) => setWin((w) => ({ ...w, end: e.target.value }))}
                disabled={!win.enabled}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white focus:outline-none focus:border-brand-blue focus:ring-4 focus:ring-brand-blue/15 disabled:opacity-40 transition-[border-color,box-shadow] duration-200 [color-scheme:dark]"
                data-testid="send-window-end"
              />
            </div>
          </div>

          <div className="flex items-center gap-2.5 mb-4">
            <button
              type="button"
              role="switch"
              aria-checked={win.enabled}
              onClick={() => setWin((w) => ({ ...w, enabled: !w.enabled }))}
              className={`relative w-10 h-6 rounded-full transition-colors duration-200 shrink-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/30 ${win.enabled ? 'bg-brand-blue' : 'bg-slate-600'}`}
              data-testid="send-window-enabled-toggle"
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 ${win.enabled ? 'translate-x-4' : ''}`} />
            </button>
            <span className="text-xs text-slate-300">{win.enabled ? 'Only send during window' : 'Window off — send anytime'}</span>
          </div>

          <button
            onClick={saveWindow}
            disabled={savingWin}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-blue text-white text-xs font-bold hover:bg-brand-blue-dark focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-blue/30 disabled:opacity-40 transition-[background-color,box-shadow] duration-200"
            data-testid="send-window-save"
          >
            {savingWin ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Window
          </button>
        </div>

        {/* Test Send */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-5" data-testid="drip-test-send-card">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical className="w-4 h-4 text-brand-orange" />
            <h3 className="text-sm font-bold text-white">Send a Test Email</h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">Preview any day in a real inbox. Marked <span className="text-slate-300 font-semibold">[TEST]</span> — never counts toward metrics.</p>

          <div className="flex items-end gap-3 mb-4">
            <div className="w-28">
              <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Day</label>
              <select
                value={testDay}
                onChange={(e) => setTestDay(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white focus:outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/15 transition-[border-color,box-shadow] duration-200 [color-scheme:dark]"
                data-testid="test-send-day"
              >
                {days.map((d) => <option key={d} value={d}>Day {d}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-[11px] font-semibold text-slate-400 mb-1 block">Recipient</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full px-3 py-2.5 rounded-lg bg-slate-900 border border-slate-700 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-brand-orange focus:ring-4 focus:ring-brand-orange/15 transition-[border-color,box-shadow] duration-200"
                data-testid="test-send-email"
              />
            </div>
          </div>

          <button
            onClick={sendTest}
            disabled={sendingTest}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-brand-orange text-white text-xs font-bold hover:bg-brand-orange-dark focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/30 disabled:opacity-40 transition-[background-color,box-shadow] duration-200"
            data-testid="test-send-submit"
          >
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Test
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 rounded-xl border border-slate-700 p-4 sm:p-5"
              data-testid={`marketing-kpi-${kpi.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              <div className={`w-9 h-9 rounded-lg ${kpi.bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-4.5 h-4.5 ${kpi.color}`} />
              </div>
              <p className="text-2xl font-bold text-white">{loading ? '...' : kpi.value}</p>
              <p className="text-xs text-slate-400 mt-0.5">{kpi.label}</p>
              {kpi.sub && <p className="text-[11px] text-slate-500 mt-0.5">{kpi.sub}</p>}
            </motion.div>
          );
        })}
      </div>

      {/* Per-day engagement — which email lands best */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6" data-testid="marketing-engagement-chart">
        <div className="flex items-center gap-2 mb-1">
          <BarChart3 className="w-4 h-4 text-brand-blue" />
          <h3 className="text-sm font-bold text-white">Which Email Lands Best</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">Opens and clicks recorded for each day's email across the whole campaign.</p>
        {loading ? (
          <div className="h-64 flex items-center justify-center text-slate-500 text-sm">Loading…</div>
        ) : !hasEngagement ? (
          <div className="h-64 flex flex-col items-center justify-center text-center text-slate-500" data-testid="engagement-empty">
            <BarChart3 className="w-8 h-8 mb-2 text-slate-600" />
            <p className="text-sm">No engagement yet.</p>
            <p className="text-xs text-slate-600 mt-1">Opens and clicks will appear here as executives engage with the sequence.</p>
          </div>
        ) : (
          <div style={{ width: '100%', height: 288 }} data-testid="engagement-recharts">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#94A3B8', fontSize: 12 }} axisLine={{ stroke: '#334155' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: '#94A3B8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <RTooltip
                  cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                  contentStyle={{ background: '#0F172A', border: '1px solid #334155', borderRadius: 12, color: '#F8FAFC', fontSize: 12 }}
                  labelStyle={{ color: '#F8FAFC', fontWeight: 700, marginBottom: 4 }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#CBD5E1' }} iconType="circle" />
                <Bar dataKey="Opens" fill="#1EACEE" radius={[5, 5, 0, 0]} maxBarSize={30} />
                <Bar dataKey="Clicks" fill="#F68D2C" radius={[5, 5, 0, 0]} maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Per-day distribution */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 mb-6" data-testid="marketing-per-day">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="w-4 h-4 text-brand-orange" />
          <h3 className="text-sm font-bold text-white">Sequence Progression</h3>
          <span className="text-xs text-slate-500">leads currently at each day</span>
        </div>
        <div className="space-y-2.5">
          {days.map((d) => {
            const count = Number(perDay[String(d)] || 0);
            const pct = Math.round((count / dayMax) * 100);
            return (
              <div key={d} className="flex items-center gap-3">
                <span className="w-12 text-xs text-slate-400 shrink-0">Day {d}</span>
                <div className="flex-1 h-2.5 rounded-full bg-slate-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-blue transition-all duration-300"
                    style={{ width: `${count ? Math.max(pct, 6) : 0}%` }}
                  />
                </div>
                <span className="w-8 text-xs font-semibold text-slate-200 text-right shrink-0">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent leads table */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden" data-testid="marketing-leads-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <h3 className="text-sm font-bold text-white">Recent Leads</h3>
          <span className="text-xs text-slate-500">{data?.recent_leads?.length || 0} shown</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-700">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden md:table-cell">Timezone</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Day</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Opens</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Clicks</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase hidden lg:table-cell">Last Sent</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading...</td></tr>
              ) : !data?.recent_leads?.length ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  <Mail className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                  No leads enrolled yet. New funnel submissions appear here.
                </td></tr>
              ) : (
                data.recent_leads.map((lead, i) => (
                  <tr key={`${lead.email}-${i}`} className="border-b border-slate-700/50" data-testid={`marketing-lead-row-${i}`}>
                    <td className="px-4 py-3 text-xs text-slate-200 max-w-[220px] truncate">
                      {lead.email}
                      {lead.first_name && <span className="text-slate-500"> · {lead.first_name}</span>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-xs text-slate-400">{lead.timezone || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-300">{lead.day_index}/7</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${STATUS_STYLES[lead.status] || 'bg-slate-700 text-slate-300'}`}>
                        {(lead.status || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-300">{lead.opens_total || 0}</td>
                    <td className="px-4 py-3 text-xs text-slate-300">{lead.clicks_total || 0}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{fmtDate(lead.last_sent_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════
   MAIN ADMIN DASHBOARD
   ═══════════════════════════════════════════ */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [pinOk, setPinOk] = useState(sessionStorage.getItem(PIN_OK_KEY) === 'true');
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState('control'); // control | pipeline | detail
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/stats`, { headers: adminHeaders() });
      setStats(resp.data);
    } catch (err) {
      if (err.response?.status === 401) { setAuthed(false); sessionStorage.removeItem('ml_admin_token'); }
    }
    // API and adminHeaders are module-level; setAuthed is a stable setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/admin/orders`, { headers: adminHeaders() });
      setOrders(resp.data);
    } catch (err) {
      if (err.response?.status === 401) { setAuthed(false); sessionStorage.removeItem('ml_admin_token'); }
    } finally {
      setLoading(false);
    }
    // API and adminHeaders are module-level; setAuthed is a stable setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) { fetchStats(); fetchOrders(); }
  }, [authed, fetchStats, fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { headers: adminHeaders() });
      fetchOrders();
      fetchStats();
      if (selectedOrder?.order_id === orderId) {
        const updated = await axios.get(`${API}/orders/${orderId}`);
        setSelectedOrder(updated.data);
      }
    } catch (err) {
      console.error('Admin: order status change failed', err);
    }
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setActiveView('detail');
  };

  const handleRefreshDetail = async () => {
    if (selectedOrder) {
      try {
        const resp = await axios.get(`${API}/orders/${selectedOrder.order_id}`);
        setSelectedOrder(resp.data);
        fetchOrders();
      } catch (err) { console.error('Admin: order detail refresh failed', err); }
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('ml_admin_token');
    sessionStorage.removeItem('ml_admin_user');
    sessionStorage.removeItem(PIN_OK_KEY);
    setAuthed(false);
    setPinOk(false);
  };

  if (!pinOk) return <AdminPinGate onUnlock={() => setPinOk(true)} />;
  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  const navItems = [
    { id: 'control', label: 'Mission Control', icon: LayoutDashboard },
    { id: 'pipeline', label: 'Order Pipeline', icon: Package },
    { id: 'projects', label: 'Project Pipeline', icon: Briefcase },
    { id: 'users', label: 'User Directory', icon: Users },
    { id: 'leads', label: 'Leads', icon: ClipboardList },
    { id: 'crm', label: 'CRM Pipeline', icon: LayoutGrid },
    { id: 'marketing', label: 'Marketing', icon: Megaphone },
    { id: 'offers', label: 'Custom Offers', icon: Gift },
    { id: 'team', label: 'Team Management', icon: Settings },
    { id: 'audit', label: 'Audit Log', icon: ClipboardList },
    { id: 'invoicing', label: 'Invoicing', icon: FileText },
    { id: 'hygiene', label: 'Data Hygiene', icon: ShieldCheck },
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <motion.aside
        animate={{ width: sidebarOpen ? 240 : 64 }}
        transition={{ duration: 0.2 }}
        className="bg-slate-800 border-r border-slate-700 flex flex-col shrink-0 sticky top-0 h-screen z-40"
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-4 flex items-center gap-3 border-b border-slate-700">
          <div className="w-8 h-8 rounded-lg bg-brand-blue flex items-center justify-center shrink-0">
            <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          {sidebarOpen && <span className="text-sm font-bold text-white truncate">MediaLabs Ops</span>}
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-4 px-2 space-y-1">
          {navItems.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => { setActiveView(id); setSelectedOrder(null); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                (activeView === id || (activeView === 'detail' && id === 'pipeline'))
                  ? 'bg-brand-blue/10 text-brand-blue font-semibold'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              data-testid={`nav-${id}`}
            >
              <Icon className="w-4.5 h-4.5 shrink-0" />
              {sidebarOpen && <span className="truncate">{label}</span>}
            </button>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-2 border-t border-slate-700 space-y-1">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {sidebarOpen && <span>Collapse</span>}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </motion.aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-y-auto">
        {/* Top bar */}
        <header
          className="sticky top-0 z-30 flex items-center justify-end gap-3 px-5 sm:px-6 lg:px-8 h-16 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 shrink-0"
          data-testid="admin-topbar"
        >
          <NotificationCenter />
        </header>
        <div className="p-5 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
          {activeView === 'control' && (
            <motion.div key="control" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <DaasMetrics />
              <MissionControl stats={stats} loading={!stats} />
            </motion.div>
          )}
          {activeView === 'projects' && (
            <motion.div key="projects" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ProjectPipelineView />
            </motion.div>
          )}
          {activeView === 'crm' && (
            <motion.div key="crm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <CrmPipelineView />
            </motion.div>
          )}
          {activeView === 'pipeline' && (
            <motion.div key="pipeline" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <OrderPipeline
                orders={orders}
                loading={loading}
                onSelectOrder={handleSelectOrder}
                onRefresh={() => { fetchOrders(); fetchStats(); }}
                onStatusChange={handleStatusChange}
              />
            </motion.div>
          )}
          {activeView === 'detail' && selectedOrder && (
            <motion.div key="detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <OrderDetail
                order={selectedOrder}
                onClose={() => { setActiveView('pipeline'); setSelectedOrder(null); }}
                onRefresh={handleRefreshDetail}
              />
            </motion.div>
          )}
          {activeView === 'offers' && (
            <motion.div key="offers" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <CustomOffersView />
            </motion.div>
          )}
          {activeView === 'users' && (
            <motion.div key="users" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <UserDirectoryView />
            </motion.div>
          )}
          {activeView === 'marketing' && (
            <motion.div key="marketing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <MarketingView />
            </motion.div>
          )}
          {activeView === 'leads' && (
            <motion.div key="leads" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <LeadsView />
            </motion.div>
          )}
          {activeView === 'team' && (
            <motion.div key="team" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <TeamManagementView />
            </motion.div>
          )}
          {activeView === 'audit' && (
            <motion.div key="audit" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <AuditLogView />
            </motion.div>
          )}
          {activeView === 'invoicing' && (
            <motion.div key="invoicing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <InvoicingView />
            </motion.div>
          )}
          {activeView === 'hygiene' && (
            <motion.div key="hygiene" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <DataHygieneView />
            </motion.div>
          )}
        </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
